<script setup lang="ts">
import { computed } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import { StayTag } from '@apps/main/new-desktop/modules/stayTag';

import { AppButton } from '@libs/ui';

import { StaySelectionType } from '../../models';
import type { Stay, StayId } from '../../models';
import { DealCodeTag } from '../../../dealCode';

interface HotelStaySummaryProps {
  stay: Stay;
  showSelectButton: boolean;
}
const props = defineProps<HotelStaySummaryProps>();

interface HotelStaySummaryEmits {
  (eventName: 'stay-selected', stayId: StayId): void;
}

const emit = defineEmits<HotelStaySummaryEmits>();

const browserStore = useBrowserStore();

const buttonType = computed(() =>
  props.stay.selectionType === StaySelectionType.ManuallySelected
    ? 'important'
    : 'secondary'
);
</script>

<template>
  <div class="hotel-stay-summary">
    <div class="stay-summary">
      <div class="hotel-stay-price-summary">
        <DealCodeTag v-if="props.stay.grossPrice" />
        <div class="hotel-stay-nights">
          {{ $t('planner.total_for').toLowerCase() }}
          {{ props.stay.numberOfNights }}
          {{ $t('planner.nights', props.stay.numberOfNights).toLowerCase() }}
        </div>

        <div class="price-section">
          <div v-if="props.stay.grossPrice" class="gross-price">
            {{ props.stay.grossPrice }}
          </div>
          <div
            class="total-price"
            :class="{ 'with-discount': props.stay.grossPrice }"
          >
            {{ props.stay.totalPrice }}
          </div>
        </div>
      </div>

      <AppButton
        :type="buttonType"
        :class="{ 'select-stay-button': true, buttonType }"
        state="enabled"
        size="medium"
        @click="emit('stay-selected', props.stay.stayId)"
      >
        {{
          props.stay.selectionType === StaySelectionType.ManuallySelected
            ? $t('planner.continue_with_this_room')
            : $t('planner.select')
        }}
      </AppButton>
    </div>
    <StayTag
      v-if="props.stay.tag"
      :stay-tag="props.stay.tag"
      :wide="browserStore.isSmallerThan1200px"
    />
  </div>
</template>

<style scoped lang="scss">
.hotel-stay-summary {
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  align-items: end;
  gap: var(--spacing-24);
  @include for-less-or-equal-than-599-px {
    align-items: normal;
    flex-direction: column;
  }

  .stay-summary {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    @include for-less-or-equal-than-599-px {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-8);
      flex-wrap: wrap;

      .important {
        margin: auto;
      }
    }

    .hotel-stay-price-summary {
      display: flex;
      flex-direction: column;
      align-self: end;
      gap: var(--spacing-4);

      @include for-more-or-equal-than-599-px {
        align-items: end;
      }

      .hotel-stay-nights {
        color: var(--color-neutral-70);
        @include font-label-s-light;
        white-space: nowrap;

        @include for-less-or-equal-than-599-px {
          white-space: normal;
        }
      }
    }

    .price-section {
      display: flex;
      align-items: center;
      gap: var(--spacing-8);

      .gross-price {
        @include font-heading-6;
        text-decoration: line-through;
        color: var(--color-neutral-70);
      }

      .total-price {
        @include font-heading-6;
        color: var(--color-neutral-70);

        &.with-discount {
          color: var(--color-error-dark);
        }
      }
    }

    .select-stay-button {
      margin-top: var(--spacing-16);

      @include for-less-or-equal-than-599-px {
        margin-top: 0;
      }
    }
  }
}
</style>
